import React from 'react';
import { Box, Container, Grid, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';
import s1 from '../../assets/images/s1.png';
import s2 from '../../assets/images/s2.png';
import s3 from '../../assets/images/s3.png';
import s4 from '../../assets/images/s4.png';

const steps = [
    {
        img: s1,
        title: 'Sign Up',
        description: 'Create an account using your username and email.'
    },
    {
        img: s2,
        title: 'Select Bundle',
        description: 'Choose the advertising bundle that fits your needs.'
    },
    {
        img: s3,
        title: 'Launch Campaign',
        description: 'Upload your assets and launch your ad campaign.'
    },
    {
        img: s4,
        title: 'Grow Sales',
        description: 'Boost your visibility and watch your sales grow.'
    }
];

const container = {
    hidden: { opacity: 0 },
    show: {
        opacity: 1,
        transition: { staggerChildren: 0.3 }
    }
};

const item = {
    hidden: { opacity: 0, x: -50 },
    show: { opacity: 1, x: 0, transition: { type: 'spring', stiffness: 50 } }
};

const HowItWorksSection = () => {
    const theme = useTheme();

    return (
        <Box id="how-it-works" sx={{ py: 10 }}>
            <Container maxWidth="lg">
                <Box sx={{ textAlign: 'center', mb: 8 }} component={motion.div} initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}>
                    <Typography variant="h6" sx={{ color: theme.palette.primary.main, mb: 2, textTransform: 'uppercase', letterSpacing: '3px', fontWeight: 700 }}>
                        Workflow
                    </Typography>
                    <Typography variant="h2" sx={{ color: theme.palette.text.primary, fontWeight: 800 }}>
                        Advertise in <span style={{ color: theme.palette.secondary.main }}>4 Easy Steps</span>
                    </Typography>
                </Box>

                <Grid
                    container
                    spacing={4}
                    component={motion.div}
                    variants={container}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                >
                    {steps.map((step, index) => (
                        <Grid item xs={12} sm={6} md={3} key={index} component={motion.div} variants={item}>
                            <Box
                                sx={{
                                    textAlign: 'center',
                                    p: 3,
                                    height: '100%',
                                    position: 'relative',
                                    background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.6)',
                                    borderRadius: '20px',
                                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.1)' : '1px solid rgba(0,0,0,0.08)',
                                    transition: 'all 0.3s ease',
                                    '&:hover': {
                                        background: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(255,255,255,0.9)',
                                        transform: 'translateY(-5px)',
                                    }
                                }}
                            >
                                <Box
                                    sx={{
                                        width: '100px',
                                        height: '100px',
                                        margin: '0 auto 24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        borderRadius: '50%',
                                        background: 'radial-gradient(circle, rgba(0, 230, 118, 0.2) 0%, rgba(0,0,0,0) 70%)',
                                    }}
                                >
                                    <img
                                        src={step.img}
                                        alt={step.title}
                                        style={{
                                            width: '60%',
                                            height: 'auto',
                                            filter: 'hue-rotate(60deg) drop-shadow(0 0 10px rgba(0,240,255,0.5))'
                                        }}
                                    />
                                </Box>
                                <Typography variant="h5" sx={{ color: theme.palette.text.primary, mb: 2, fontWeight: 700 }}>
                                    {step.title}
                                </Typography>
                                <Typography variant="body1" sx={{ color: theme.palette.text.secondary }}>
                                    {step.description}
                                </Typography>

                                {/* Step Number Watermark */}
                                <Typography
                                    sx={{
                                        position: 'absolute',
                                        top: 10,
                                        right: 20,
                                        fontSize: '4rem',
                                        fontWeight: 900,
                                        color: 'rgba(0, 230, 118, 0.1)',
                                        lineHeight: 1,
                                    }}
                                >
                                    {index + 1}
                                </Typography>
                            </Box>
                        </Grid>
                    ))}
                </Grid>
            </Container>
        </Box>
    );
};

export default HowItWorksSection;
